import React, { useId, useMemo, useState } from "react";
import { cn } from "../../libs/utils";

/**
 * Generic, dependency-free multi-series line/area chart.
 *
 * The project has no charting library installed, so this renders plain SVG
 * rather than pulling in a new dependency for a single screen. It is
 * intentionally generic (categories + series[]) so any future screen that
 * needs a trend line (weld parameters, machine KPIs, dispatch trends, etc.)
 * can reuse this component instead of building another one-off chart.
 *
 * @param {string[]} categories - x-axis labels, one per data point
 * @param {{ key: string, label: string, color: string, data: number[], unit?: string }[]} series
 * @param {{ value: number, label?: string, color?: string }[]} [thresholds] - optional horizontal reference lines
 */
export default function LineChart({
  categories = [],
  series = [],
  thresholds = [],
  legend,
  height = 260,
  className,
  emptyLabel = "No data available for this range.",
}) {
  const gradientId = useId();
  const [hoverIndex, setHoverIndex] = useState(null);
  const padding = { top: 16, right: 16, bottom: 28, left: 40 };
  const width = 640;

  const allValues = useMemo(
    () => series.flatMap((s) => s.data).concat(thresholds.map((t) => t.value)),
    [series, thresholds]
  );

  const hasData = categories.length > 0 && series.some((s) => s.data.length > 0);

  const { min, max } = useMemo(() => {
    if (!allValues.length) return { min: 0, max: 1 };
    const dataMin = Math.min(...allValues);
    const dataMax = Math.max(...allValues);
    const pad = (dataMax - dataMin) * 0.15 || dataMax * 0.1 || 1;
    return { min: dataMin - pad, max: dataMax + pad };
  }, [allValues]);

  const plotWidth = width - padding.left - padding.right;
  const plotHeight = height - padding.top - padding.bottom;

  const xForIndex = (index) =>
    padding.left +
    (categories.length > 1 ? (index / (categories.length - 1)) * plotWidth : plotWidth / 2);

  const yForValue = (value) =>
    padding.top + plotHeight - ((value - min) / (max - min || 1)) * plotHeight;

  const buildPath = (data) =>
    data
      .map((value, index) => `${index === 0 ? "M" : "L"} ${xForIndex(index)} ${yForValue(value)}`)
      .join(" ");

  const buildAreaPath = (data) => {
    if (!data.length) return "";
    const line = buildPath(data);
    const lastX = xForIndex(data.length - 1);
    const firstX = xForIndex(0);
    return `${line} L ${lastX} ${padding.top + plotHeight} L ${firstX} ${padding.top + plotHeight} Z`;
  };

  const yTicks = useMemo(() => {
    const tickCount = 4;
    return Array.from({ length: tickCount + 1 }, (_, i) => min + ((max - min) * i) / tickCount);
  }, [min, max]);

  if (!hasData) {
    return (
      <div
        className={cn(
          "flex items-center justify-center rounded-lg border border-dashed border-slate-200 text-sm text-slate-400",
          className
        )}
        style={{ height }}
      >
        {emptyLabel}
      </div>
    );
  }

  return (
    <div className={cn("w-full", className)}>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="h-auto w-full"
        role="img"
        aria-label="Trend chart"
        onMouseLeave={() => setHoverIndex(null)}
      >
        {yTicks.map((tick, i) => (
          <g key={i}>
            <line
              x1={padding.left}
              x2={width - padding.right}
              y1={yForValue(tick)}
              y2={yForValue(tick)}
              stroke="#eef2f7"
              strokeWidth={1}
            />
            <text x={padding.left - 8} y={yForValue(tick)} textAnchor="end" dominantBaseline="middle" fontSize={10} fill="#94a3b8">
              {Number.isInteger(tick) ? tick : tick.toFixed(1)}
            </text>
          </g>
        ))}

        {thresholds.map((t, i) => (
          <g key={`threshold-${i}`}>
            <line
              x1={padding.left}
              x2={width - padding.right}
              y1={yForValue(t.value)}
              y2={yForValue(t.value)}
              stroke={t.color || "#ef4444"}
              strokeWidth={1.5}
              strokeDasharray="4 4"
            />
            {t.label && (
              <text x={width - padding.right} y={yForValue(t.value) - 4} textAnchor="end" fontSize={10} fill={t.color || "#ef4444"}>
                {t.label}
              </text>
            )}
          </g>
        ))}

        {categories.map((label, index) => {
          if (categories.length > 8 && index % Math.ceil(categories.length / 8) !== 0) return null;
          return (
            <text
              key={label + index}
              x={xForIndex(index)}
              y={height - 8}
              textAnchor="middle"
              fontSize={10}
              fill="#94a3b8"
            >
              {label}
            </text>
          );
        })}

        {series.map((s) => (
          <g key={s.key}>
            <defs>
              <linearGradient id={`${gradientId}-${s.key}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={s.color} stopOpacity="0.22" />
                <stop offset="100%" stopColor={s.color} stopOpacity="0" />
              </linearGradient>
            </defs>
            <path d={buildAreaPath(s.data)} fill={`url(#${gradientId}-${s.key})`} stroke="none" />
            <path d={buildPath(s.data)} fill="none" stroke={s.color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
            {s.data.map((value, index) => (
              <circle
                key={index}
                cx={xForIndex(index)}
                cy={yForValue(value)}
                r={hoverIndex === index ? 4 : 2.5}
                fill="#ffffff"
                stroke={s.color}
                strokeWidth={2}
              />
            ))}
          </g>
        ))}

        {categories.map((_, index) => (
          <rect
            key={`hit-${index}`}
            x={xForIndex(index) - plotWidth / Math.max(categories.length, 1) / 2}
            y={padding.top}
            width={plotWidth / Math.max(categories.length, 1)}
            height={plotHeight}
            fill="transparent"
            onMouseEnter={() => setHoverIndex(index)}
          />
        ))}

        {hoverIndex !== null && (
          <line
            x1={xForIndex(hoverIndex)}
            x2={xForIndex(hoverIndex)}
            y1={padding.top}
            y2={padding.top + plotHeight}
            stroke="#cbd5f5"
            strokeWidth={1}
          />
        )}
      </svg>

      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1.5">
        {(legend || series).map((item) => (
          <span key={item.key || item.label} className="flex items-center gap-1.5 text-xs font-medium text-slate-600">
            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: item.color }} />
            {item.label}
            {!legend && hoverIndex !== null && item.data?.[hoverIndex] !== undefined && (
              <span className="font-mono text-slate-400">
                {item.data[hoverIndex]}
                {item.unit || ""}
              </span>
            )}
          </span>
        ))}
      </div>
    </div>
  );
}
